// AdminDashboard.js v3.8 - Time Tracking + NotificationBell + Project Map System navigation
// Fetches users directly from Google Sheets CSV export
import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, LayoutDashboard, Users, Briefcase, DollarSign, FileText, Settings, ChevronRight, CheckCircle, XCircle, Search, Building2, Eye, MapPin, UserCog, Target, Shovel, BarChart3, History, User, RefreshCw, Loader, Menu, X, Sun, Moon, Map, Upload, FolderOpen, UserPlus, Shield, FlaskConical, Clipboard, Clock, AlertTriangle, Download, Edit3, MoreHorizontal, Home } from 'lucide-react';
import { format, parseISO, getISOWeek, getISOWeekYear, startOfISOWeek, addDays } from 'date-fns';
import { colors } from '../config/constants';
import { fetchWithRedirect } from '../utils/fetchHelpers';
import { submitTestContractor, submitTestEmployee } from '../services/testSubmissions';
import { loadDashboardStats, loadProjectsList } from '../services/mapService';
import NotificationBell from '../components/NotificationBell';

// Direct Google Sheets CSV URLs (for reading data)
const USERS_SHEET_CSV = 'https://docs.google.com/spreadsheets/d/1OjSak2YJJvbXjyX3FSND_GfaQUZ2IQkFiMRgLuNfqVw/gviz/tq?tqx=out:csv';
const ONBOARDING_SHEET_CSV = 'https://docs.google.com/spreadsheets/d/1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc/gviz/tq?tqx=out:csv';

// Gateway URL for write operations (approve/reject)
const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const ONBOARDING_SHEET_ID = '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc';

// Parse CSV text into array of objects
const parseCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];
  
  // Parse header row (handle quoted values)
  const parseRow = (row) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };
  
  const headers = parseRow(lines[0]);
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = parseRow(lines[i]);
    const obj = {};
    headers.forEach((header, idx) => {
      obj[header] = values[idx] || '';
    });
    data.push(obj);
  }
  
  return data;
};

const AdminDashboard = ({ setCurrentPage, loggedInUser, setLoggedInUser, darkMode, setDarkMode, setReviewContractor }) => {
  // Dynamic colors based on theme
  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const accentSecondary = darkMode ? '#ff6b35' : '#28a745';
  const accentError = darkMode ? '#ff6b6b' : '#e85a4f';

  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : colors.dark;
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';

  // State for real data
  const [users, setUsers] = useState([]);
  const [onboardingSubmissions, setOnboardingSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const [activeTab, setActiveTabRaw] = useState(() => {
    try { return sessionStorage.getItem('admin-tab') || 'dashboard'; } catch { return 'dashboard'; }
  });
  const setActiveTab = useCallback((tab) => {
    setActiveTabRaw(prev => {
      if (prev === tab) return prev;
      try { sessionStorage.setItem('admin-tab', tab); } catch { /* noop */ }
      const currentState = window.history.state || {};
      window.history.pushState({ ...currentState, adminTab: tab }, '');
      return tab;
    });
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [showVersion, setShowVersion] = useState(false);

  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Dashboard stats (from Postgres - projects, segments, billing)
  const [dashStats, setDashStats] = useState(null);
  const [projectsList, setProjectsList] = useState([]);

  // Time tracking state
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeTrackingSubTab, setTimeTrackingSubTab] = useState('live');
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ clockIn: '', clockOut: '', project: '', notes: '', breakMinutes: '' });

  // Browser back/forward for tab navigation
  useEffect(() => {
    const onPopState = (e) => {
      if (e.state && e.state.adminTab && e.state.page === 'admin-dashboard') {
        setActiveTabRaw(e.state.adminTab);
        try { sessionStorage.setItem('admin-tab', e.state.adminTab); } catch { /* noop */ }
      }
    };
    window.addEventListener('popstate', onPopState);
    if (!window.history.state?.adminTab) {
      window.history.replaceState({ ...window.history.state, adminTab: activeTab }, '');
    }
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false); // Close sidebar when switching to desktop
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch real data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch users directly from Google Sheets CSV export
      const usersResponse = await fetch(USERS_SHEET_CSV);
      const usersCSV = await usersResponse.text();
      const usersData = parseCSV(usersCSV);
      
      // Filter out empty rows and map to expected format
      const validUsers = usersData
        .filter(u => u.email && u.email.includes('@'))
        .map(u => ({
          email: u.email,
          name: u.name,
          role: u.role,
          status: u.status || 'active',
          createdAt: u.createdAt,
          lastLogin: u.lastLogin,
          invitedBy: u.invitedBy
        }));
      
      setUsers(validUsers);

      // Try to fetch onboarding data (may fail if sheet is private)
      try {
        const onboardingResponse = await fetch(ONBOARDING_SHEET_CSV);
        const onboardingCSV = await onboardingResponse.text();
        
        // Check if we got HTML (login page) instead of CSV
        if (!onboardingCSV.includes('<!DOCTYPE') && !onboardingCSV.includes('<html')) {
          const onboardingData = parseCSV(onboardingCSV);
          const submissions = onboardingData
            .filter(row => row.Timestamp || row.timestamp)
            .map((row, idx) => ({
              id: idx + 1,
              timestamp: row.Timestamp || row.timestamp || '',
              type: row.Type || row.type || '',
              name: row.Name || row.name || row['Company'] || '',
              email: row.Email || row.email || '',
              phone: row.Phone || row.phone || '',
              status: row.Status || row.status || 'Pending',
              folderLink: row['Folder Link'] || row.folderLink || ''
            }))
            .filter(s => s.name && !s.name.includes('Unknown'));
          
          setOnboardingSubmissions(submissions);
        }
      } catch (onboardingErr) {
        console.log('Onboarding sheet not accessible:', onboardingErr);
        // Onboarding data unavailable - that's ok, just show users
      }

      setLastRefresh(new Date());
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load data. Please refresh.');
    }

    setLoading(false);

    // Fetch time entries (async, non-blocking)
    fetchTimeEntries();

    // Fetch project/segment/billing stats from Postgres (async, non-blocking)
    loadDashboardStats().then(stats => { if (stats) setDashStats(stats); });
    loadProjectsList().then(projects => { if (projects) setProjectsList(projects); });
  };

  const fetchTimeEntries = async () => {
    try {
      const text = await fetchWithRedirect(GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          secret: GATEWAY_SECRET,
          action: 'sheetsRead',
          params: { spreadsheetId: ONBOARDING_SHEET_ID, range: 'TimeEntries!A:R' }
        })
      });
      const result = JSON.parse(text);
      if (result.success && result.data?.data && result.data.data.length > 1) {
        setTimeEntries(result.data.data.slice(1)); // skip header
      }
    } catch (e) {
      console.error('Failed to fetch time entries:', e);
    }
  };


  const handleLogout = () => {
    setLoggedInUser(null);
    setCurrentPage('portal-login');
  };

  // Computed values from real data
  const employees = users.filter(u => u.role === 'employee');
  const contractors = users.filter(u => u.role === 'contractor');
  const admins = users.filter(u => u.role === 'admin');
  const pendingOnboarding = onboardingSubmissions.filter(s => s.status !== 'Approved' && s.status !== 'Rejected');
  const recentSubmissions = onboardingSubmissions.slice(0, 10);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'profile', label: 'My Profile', icon: User, external: 'profile' },
    { id: 'metrics', label: 'Metrics Dashboard', icon: BarChart3, external: 'metrics' },
    { id: 'activity-log', label: 'Activity Log', icon: History, external: 'activity-log' },
    { id: 'onboarding', label: 'Pending Onboarding', icon: UserPlus, badge: pendingOnboarding.length },
    { id: 'recruiting', label: 'Recruiting Pipeline', icon: Target, external: 'recruiting' },
    { id: 'divider-maps', label: '── Project Maps ──', divider: true },
    { id: 'project-map', label: 'Interactive Map', icon: Map, external: 'project-map' },
    { id: 'admin-projects', label: 'Projects Overview', icon: FolderOpen, external: 'admin-projects' },
    { id: 'job-import', label: 'Import Work Orders', icon: Upload, external: 'job-import' },
    { id: 'json-import', label: 'Import JSON Data', icon: Clipboard, external: 'json-import' },
    { id: 'daily-report', label: 'Daily Report', icon: FileText, external: 'daily-report' },
    { id: 'divider-field', label: '── Field Operations ──', divider: true },
    { id: 'potholes', label: 'Pothole Approvals', icon: Shovel, external: 'potholes' },
    { id: 'segment-reassign', label: 'Reassign Segments', icon: RefreshCw, external: 'segment-reassign' },
    { id: 'asset-management', label: 'Assets & Equipment', icon: Briefcase, external: 'asset-management' },
    { id: 'street-sheet', label: 'Street Sheets', icon: MapPin, external: 'street-sheet' },
    { id: 'daily-worksheet', label: 'Daily Work Sheets', icon: FileText, external: 'daily-worksheet' },
    { id: 'invoice-gen', label: 'Invoice Generator', icon: DollarSign, external: 'invoices' },
    { id: 'divider-billing', label: '── Billing & Payments ──', divider: true },
    { id: 'payment-tracker', label: 'Payment Tracker', icon: DollarSign, external: 'payment-tracker' },
    { id: 'payment-invoices', label: 'Client Invoices', icon: FileText, external: 'payment-invoices' },
    { id: 'divider-mgmt', label: '── Management ──', divider: true },
    { id: 'time-tracking', label: 'Time Tracking', icon: Clock },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'contractors', label: 'Contractors', icon: Briefcase },
    { id: 'user-management', label: 'User Management', icon: UserCog, external: 'admin-users' },
    { id: 'admins', label: 'Admin Users', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'testing', label: 'Test Submissions', icon: FlaskConical },
    { id: 'divider-help', label: '── Help ──', divider: true },
    { id: 'user-guide', label: 'User Guide', icon: FileText, external: 'user-guide' },
  ];

  const bottomTabs = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'onboarding', label: 'Onboard', icon: UserPlus, badge: pendingOnboarding.length },
    { id: 'time-tracking', label: 'Time', icon: Clock },
    { id: 'employees', label: 'People', icon: Users },
    { id: 'more', label: 'More', icon: MoreHorizontal },
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'active':
      case 'completed':
        return accentSecondary;
      case 'pending':
        return '#f59e0b';
      case 'rejected':
        return accentError;
      default:
        return mutedColor;
    }
  };

  const handleApproveOnboarding = async (submission) => {
    try {
      // Update the sheet - find the row and update status column
      const rowNumber = submission.id + 1; // +1 for header row
      await fetchWithRedirect(GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: GATEWAY_SECRET,
          action: 'sheetsUpdate',
          params: {
            spreadsheetId: ONBOARDING_SHEET_ID,
            range: `O${rowNumber}`,
            values: [['Approved']]
          }
        })
      });
      setOnboardingSubmissions(prev => 
        prev.map(s => s.id === submission.id ? { ...s, status: 'Approved' } : s)
      );
      alert(`Approved: ${submission.name}`);
    } catch (err) {
      console.error('Failed to approve:', err);
      alert('Failed to update status');
    }
  };

  const handleRejectOnboarding = async (submission) => {
    const reason = prompt('Reason for rejection (optional):');
    try {
      const rowNumber = submission.id + 1;
      await fetchWithRedirect(GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: GATEWAY_SECRET,
          action: 'sheetsUpdate',
          params: {
            spreadsheetId: ONBOARDING_SHEET_ID,
            range: `O${rowNumber}:P${rowNumber}`,
            values: [['Rejected', reason || '']]
          }
        })
      });
      setOnboardingSubmissions(prev => 
        prev.map(s => s.id === submission.id ? { ...s, status: 'Rejected' } : s)
      );
      alert(`Rejected: ${submission.name}`);
    } catch (err) {
      console.error('Failed to reject:', err);
      alert('Failed to update status');
    }
  };

  const renderDashboard = () => (
    <div>
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Admin Dashboard</h2>
          <p style={{ color: mutedColor }}>
            Overview of operations and pending approvals
            {lastRefresh && <span style={{ marginLeft: '16px', fontSize: '0.8rem' }}>Last updated: {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </div>
        <button
          onClick={fetchAllData}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: accentPrimary,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? <Loader size={18} className="spin" /> : <RefreshCw size={18} />}
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" style={{ padding: '16px', backgroundColor: `${accentError}20`, borderRadius: '8px', marginBottom: '24px', color: accentError }}>
          {error}
        </div>
      )}

      {/* Stats Grid - Real Data */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Total Users', value: users.length, icon: Users, color: accentPrimary },
          { label: 'Employees', value: employees.length, icon: Users, color: accentPrimary },
          { label: 'Contractors', value: contractors.length, icon: Briefcase, color: accentPrimary },
          { label: 'Admins', value: admins.length, icon: Shield, color: accentSecondary },
          { label: 'Clocked In Now', value: timeEntries.filter(r => r[12] === 'Active').length, icon: Clock, color: accentSecondary, onClick: () => setActiveTab('time-tracking') },
          { label: 'Pending Onboarding', value: pendingOnboarding.length, icon: UserPlus, color: accentError, onClick: () => setActiveTab('onboarding') },
        ].map((stat, idx) => (
          <div 
            key={idx} 
            onClick={stat.onClick}
            style={{ 
              backgroundColor: cardBg, 
              borderRadius: '12px', 
              padding: '20px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              cursor: stat.onClick ? 'pointer' : 'default',
              transition: 'transform 0.2s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${stat.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <stat.icon size={20} color={stat.color} />
              </div>
              {stat.onClick && <ChevronRight size={16} color={mutedColor} />}
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: textColor }}>{loading ? '...' : stat.value}</div>
            <div style={{ fontSize: '0.85rem', color: mutedColor }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Projects & Billing Stats */}
      {dashStats && (
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '16px' }}>Projects & Production</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
            {[
              { label: 'Total Segments', value: dashStats.segments.total.toLocaleString(), sub: `${Math.round(dashStats.segments.totalFootage).toLocaleString()} ft`, icon: MapPin, color: accentPrimary },
              { label: 'Boring Progress', value: `${dashStats.segments.total ? Math.round(((dashStats.segments.boring.complete + dashStats.segments.boring.qcApproved) / dashStats.segments.total) * 100) : 0}%`, sub: `${dashStats.segments.boring.complete + dashStats.segments.boring.qcApproved} / ${dashStats.segments.total}`, icon: Shovel, color: '#10b981' },
              { label: 'Pulling Progress', value: `${dashStats.segments.total ? Math.round(((dashStats.segments.pulling.complete + dashStats.segments.pulling.qcApproved) / dashStats.segments.total) * 100) : 0}%`, sub: `${dashStats.segments.pulling.complete + dashStats.segments.pulling.qcApproved} / ${dashStats.segments.total}`, icon: Target, color: '#3b82f6' },
              { label: 'Total Billed', value: `$${Math.round(dashStats.billing.totalBilled).toLocaleString()}`, icon: DollarSign, color: '#f59e0b' },
              { label: 'Total Paid', value: `$${Math.round(dashStats.billing.totalPaid).toLocaleString()}`, icon: DollarSign, color: accentSecondary },
              { label: 'Invoices Pending', value: dashStats.billing.invoicesPending, sub: `of ${dashStats.billing.invoiceCount} total`, icon: FileText, color: accentError },
            ].map((stat, idx) => (
              <div key={idx} style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${stat.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <stat.icon size={20} color={stat.color} />
                  </div>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: textColor }}>{stat.value}</div>
                <div style={{ fontSize: '0.85rem', color: mutedColor }}>{stat.label}</div>
                {stat.sub && <div style={{ fontSize: '0.75rem', color: mutedColor, marginTop: '2px' }}>{stat.sub}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Project Overview Table */}
      {projectsList.length > 0 && dashStats && (
        <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor }}>Project Overview</h3>
            <button
              onClick={() => setCurrentPage('admin-projects')}
              style={{ color: accentPrimary, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              Full Dashboard →
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Project</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Client</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>WO #</th>
                </tr>
              </thead>
              <tbody>
                {projectsList.map((proj) => (
                  <tr
                    key={proj.id}
                    onClick={() => { window.location.hash = `#project-map?project=${proj.id}`; }}
                    style={{ borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = darkMode ? '#1e293b' : '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '12px', color: textColor, fontWeight: '500' }}>{proj.name}</td>
                    <td style={{ padding: '12px', color: mutedColor }}>{proj.client}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        backgroundColor: proj.status === 'Active' ? `${accentSecondary}20` : `${mutedColor}20`,
                        color: proj.status === 'Active' ? accentSecondary : mutedColor
                      }}>
                        {proj.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: mutedColor, textAlign: 'right', fontSize: '0.85rem' }}>{proj.workOrder || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Onboarding Submissions */}
      <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor }}>Recent Onboarding Submissions</h3>
          <button 
            onClick={() => setActiveTab('onboarding')}
            style={{ color: accentPrimary, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            View All →
          </button>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: mutedColor }}>Loading...</div>
        ) : recentSubmissions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: mutedColor }}>No submissions yet</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: mutedColor, fontWeight: '500', fontSize: '0.85rem' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentSubmissions.slice(0, 5).map((sub) => (
                  <tr key={sub.id} style={{ borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                    <td style={{ padding: '12px', color: textColor }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem',
                        backgroundColor: sub.type === 'Employee' ? `${accentPrimary}20` : `${accentSecondary}20`,
                        color: sub.type === 'Employee' ? accentPrimary : accentSecondary
                      }}>
                        {sub.type}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: textColor, fontWeight: '500' }}>{sub.name}</td>
                    <td style={{ padding: '12px', color: mutedColor }}>{sub.email}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem',
                        backgroundColor: `${getStatusColor(sub.status)}20`,
                        color: getStatusColor(sub.status)
                      }}>
                        {sub.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: mutedColor, fontSize: '0.85rem' }}>
                      {new Date(sub.timestamp).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Users Overview */}
      <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor }}>Portal Users</h3>
          <button 
            onClick={() => setCurrentPage('admin-users')}
            style={{ color: accentPrimary, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            Manage Users →
          </button>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: mutedColor }}>Loading...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
            {users.slice(0, 6).map((user) => (
              <div key={user.email} style={{ 
                padding: '12px', 
                borderRadius: '8px', 
                backgroundColor: darkMode ? '#1e293b' : '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  backgroundColor: `${accentPrimary}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: accentPrimary,
                  fontWeight: '600'
                }}>
                  {user.name?.charAt(0) || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '500', color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
                  <div style={{ fontSize: '0.8rem', color: mutedColor }}>{user.role}</div>
                </div>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: user.status === 'active' ? accentSecondary : mutedColor 
                }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderOnboarding = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Pending Onboarding</h2>
        <p style={{ color: mutedColor }}>Review and approve new employee and contractor submissions</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: mutedColor }}>
          <Loader size={32} style={{ marginBottom: '16px' }} />
          <div>Loading submissions...</div>
        </div>
      ) : onboardingSubmissions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: cardBg, borderRadius: '12px', color: mutedColor }}>
          <UserPlus size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <div>No onboarding submissions yet</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {onboardingSubmissions.map((sub) => (
            <div key={sub.id} style={{ 
              backgroundColor: cardBg, 
              borderRadius: '12px', 
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    backgroundColor: sub.type === 'Employee' ? `${accentPrimary}20` : `${accentSecondary}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {sub.type === 'Employee' ? <User size={24} color={accentPrimary} /> : <Building2 size={24} color={accentSecondary} />}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '1.1rem', color: textColor }}>{sub.name}</div>
                    <div style={{ color: mutedColor, fontSize: '0.9rem' }}>{sub.email}</div>
                  </div>
                </div>
                <span style={{ 
                  padding: '6px 12px', 
                  borderRadius: '6px', 
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  backgroundColor: `${getStatusColor(sub.status)}20`,
                  color: getStatusColor(sub.status)
                }}>
                  {sub.status}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '4px' }}>Type</div>
                  <div style={{ color: textColor }}>{sub.type}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '4px' }}>Phone</div>
                  <div style={{ color: textColor }}>{sub.phone || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '4px' }}>Submitted</div>
                  <div style={{ color: textColor }}>{new Date(sub.timestamp).toLocaleDateString()}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                {sub.folderLink && (
                  <a 
                    href={sub.folderLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '8px 16px', 
                      borderRadius: '6px', 
                      border: `1px solid ${accentPrimary}`,
                      color: accentPrimary,
                      backgroundColor: 'transparent',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <Eye size={16} /> View Documents
                  </a>
                )}
                {sub.status !== 'Approved' && sub.status !== 'Rejected' && (
                  <>
                    <button
                      onClick={() => handleRejectOnboarding(sub)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: `1px solid ${accentError}`,
                        color: accentError,
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.9rem'
                      }}
                    >
                      <XCircle size={16} /> Reject
                    </button>
                    {sub.type === 'Contractor' ? (
                      <button
                        onClick={() => {
                          if (setReviewContractor) setReviewContractor(sub);
                          setCurrentPage('contractor-review');
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          color: '#fff',
                          backgroundColor: accentPrimary,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.9rem'
                        }}
                      >
                        <CheckCircle size={16} /> Review Checklist
                      </button>
                    ) : (
                      <button
                        onClick={() => handleApproveOnboarding(sub)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          color: '#fff',
                          backgroundColor: accentSecondary,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.9rem'
                        }}
                      >
                        <CheckCircle size={16} /> Approve
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderEmployees = () => (
    <div>
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Employees</h2>
          <p style={{ color: mutedColor }}>{employees.length} registered employees</p>
        </div>
      </div>

      <div style={{ backgroundColor: cardBg, borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ padding: '16px', borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: mutedColor }} />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 10px 10px 40px',
                border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                borderRadius: '8px',
                backgroundColor: darkMode ? '#1e293b' : '#fff',
                color: textColor,
                outline: 'none'
              }}
            />
          </div>
        </div>

        {employees
          .filter(e => e.name?.toLowerCase().includes(searchTerm.toLowerCase()) || e.email?.toLowerCase().includes(searchTerm.toLowerCase()))
          .map((employee, idx) => (
          <div key={employee.email} style={{ 
            padding: '16px 20px', 
            borderBottom: idx < employees.length - 1 ? `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{ 
              width: '44px', 
              height: '44px', 
              borderRadius: '50%', 
              backgroundColor: `${accentPrimary}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accentPrimary,
              fontWeight: '600',
              fontSize: '1.1rem'
            }}>
              {employee.name?.charAt(0) || '?'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '500', color: textColor }}>{employee.name}</div>
              <div style={{ fontSize: '0.85rem', color: mutedColor }}>{employee.email}</div>
            </div>
            <div style={{ 
              padding: '4px 10px', 
              borderRadius: '20px', 
              fontSize: '0.75rem',
              backgroundColor: employee.status === 'active' ? `${accentSecondary}20` : `${mutedColor}20`,
              color: employee.status === 'active' ? accentSecondary : mutedColor
            }}>
              {employee.status}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContractors = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Contractors</h2>
        <p style={{ color: mutedColor }}>{contractors.length} registered contractors</p>
      </div>

      <div style={{ backgroundColor: cardBg, borderRadius: '12px', overflow: 'hidden' }}>
        {contractors.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: mutedColor }}>
            <Briefcase size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <div>No contractors registered yet</div>
          </div>
        ) : contractors.map((contractor, idx) => (
          <div key={contractor.email} style={{ 
            padding: '16px 20px', 
            borderBottom: idx < contractors.length - 1 ? `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{ 
              width: '44px', 
              height: '44px', 
              borderRadius: '8px', 
              backgroundColor: `${accentSecondary}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Building2 size={22} color={accentSecondary} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '500', color: textColor }}>{contractor.name}</div>
              <div style={{ fontSize: '0.85rem', color: mutedColor }}>{contractor.email}</div>
            </div>
            <div style={{ 
              padding: '4px 10px', 
              borderRadius: '20px', 
              fontSize: '0.75rem',
              backgroundColor: contractor.status === 'active' ? `${accentSecondary}20` : `${mutedColor}20`,
              color: contractor.status === 'active' ? accentSecondary : mutedColor
            }}>
              {contractor.status}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAdmins = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Admin Users</h2>
        <p style={{ color: mutedColor }}>{admins.length} administrators with full access</p>
      </div>

      <div style={{ backgroundColor: cardBg, borderRadius: '12px', overflow: 'hidden' }}>
        {admins.map((admin, idx) => (
          <div key={admin.email} style={{ 
            padding: '16px 20px', 
            borderBottom: idx < admins.length - 1 ? `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{ 
              width: '44px', 
              height: '44px', 
              borderRadius: '50%', 
              backgroundColor: `${accentError}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Shield size={22} color={accentError} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '500', color: textColor }}>{admin.name}</div>
              <div style={{ fontSize: '0.85rem', color: mutedColor }}>{admin.email}</div>
            </div>
            <div style={{ fontSize: '0.8rem', color: mutedColor }}>
              Last login: {admin.lastLogin ? new Date(admin.lastLogin).toLocaleDateString() : 'Never'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ===== TEST SUBMISSIONS =====
  const [testStatus, setTestStatus] = useState({ contractor: null, employee: null });
  const [testLogs, setTestLogs] = useState({ contractor: [], employee: [] });
  const [testRunning, setTestRunning] = useState({ contractor: false, employee: false });

  const handleTestContractor = async () => {
    setTestRunning(prev => ({ ...prev, contractor: true }));
    setTestLogs(prev => ({ ...prev, contractor: [] }));
    setTestStatus(prev => ({ ...prev, contractor: null }));
    try {
      const result = await submitTestContractor((msg) => {
        setTestLogs(prev => ({ ...prev, contractor: [...prev.contractor, msg] }));
      });
      setTestStatus(prev => ({ ...prev, contractor: result.success ? 'success' : 'error' }));
    } catch (err) {
      setTestStatus(prev => ({ ...prev, contractor: 'error' }));
    }
    setTestRunning(prev => ({ ...prev, contractor: false }));
  };

  const handleTestEmployee = async () => {
    setTestRunning(prev => ({ ...prev, employee: true }));
    setTestLogs(prev => ({ ...prev, employee: [] }));
    setTestStatus(prev => ({ ...prev, employee: null }));
    try {
      const result = await submitTestEmployee((msg) => {
        setTestLogs(prev => ({ ...prev, employee: [...prev.employee, msg] }));
      });
      setTestStatus(prev => ({ ...prev, employee: result.success ? 'success' : 'error' }));
    } catch (err) {
      setTestStatus(prev => ({ ...prev, employee: 'error' }));
    }
    setTestRunning(prev => ({ ...prev, employee: false }));
  };

  const renderTesting = () => (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Test Submissions</h2>
      <p style={{ color: colors.gray, marginBottom: '24px' }}>Generate fully-filled test contractor and employee submissions with all PDFs and signatures.</p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
        {/* Contractor Test */}
        <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <Building2 size={24} color={accentPrimary} />
            <h3 style={{ margin: 0, color: textColor, fontSize: '1.1rem' }}>Test Contractor</h3>
          </div>
          <p style={{ color: colors.gray, fontSize: '0.9rem', marginBottom: '16px' }}>
            Submits "Test Contractor LLC" with W-9, MSA, Rate Card, Insurance, Fleet, Skills, Direct Deposit, and Safety PDFs.
          </p>
          <button
            onClick={handleTestContractor}
            disabled={testRunning.contractor}
            style={{
              padding: '10px 20px',
              backgroundColor: testRunning.contractor ? '#9ca3af' : accentPrimary,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: testRunning.contractor ? 'wait' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {testRunning.contractor ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating PDFs...</> : 'Run Contractor Test'}
          </button>
          {testLogs.contractor.length > 0 && (
            <div style={{ marginTop: '12px', padding: '10px', backgroundColor: darkMode ? '#1a1a2e' : '#f1f5f9', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
              {testLogs.contractor.map((log, i) => (
                <div key={i} style={{ color: log.includes('ERROR') || log.includes('FAILED') ? '#ef4444' : log.includes('SUCCESS') ? '#22c55e' : (darkMode ? '#d1d5db' : '#475569'), marginBottom: '2px' }}>{log}</div>
              ))}
            </div>
          )}
          {testStatus.contractor === 'success' && <div style={{ marginTop: '10px', color: '#22c55e', fontWeight: '600' }}>Submitted - check Google Drive + email</div>}
          {testStatus.contractor === 'error' && <div style={{ marginTop: '10px', color: '#ef4444', fontWeight: '600' }}>Failed - check console for details</div>}
        </div>

        {/* Employee Test */}
        <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <User size={24} color={accentSecondary} />
            <h3 style={{ margin: 0, color: textColor, fontSize: '1.1rem' }}>Test Employee</h3>
          </div>
          <p style={{ color: colors.gray, fontSize: '0.9rem', marginBottom: '16px' }}>
            Submits "Jane TestEmployee" with W-4, Direct Deposit, Emergency Contact, Background Check, Drug Test, and Safety PDFs.
          </p>
          <button
            onClick={handleTestEmployee}
            disabled={testRunning.employee}
            style={{
              padding: '10px 20px',
              backgroundColor: testRunning.employee ? '#9ca3af' : accentSecondary,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: testRunning.employee ? 'wait' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {testRunning.employee ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating PDFs...</> : 'Run Employee Test'}
          </button>
          {testLogs.employee.length > 0 && (
            <div style={{ marginTop: '12px', padding: '10px', backgroundColor: darkMode ? '#1a1a2e' : '#f1f5f9', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
              {testLogs.employee.map((log, i) => (
                <div key={i} style={{ color: log.includes('ERROR') || log.includes('FAILED') ? '#ef4444' : log.includes('SUCCESS') ? '#22c55e' : (darkMode ? '#d1d5db' : '#475569'), marginBottom: '2px' }}>{log}</div>
              ))}
            </div>
          )}
          {testStatus.employee === 'success' && <div style={{ marginTop: '10px', color: '#22c55e', fontWeight: '600' }}>Submitted - check Google Drive + email</div>}
          {testStatus.employee === 'error' && <div style={{ marginTop: '10px', color: '#ef4444', fontWeight: '600' }}>Failed - check console for details</div>}
        </div>
      </div>
    </div>
  );

  // ========================
  // TIME TRACKING
  // ========================

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const currentWeekStr = `${getISOWeekYear(new Date())}-W${String(getISOWeek(new Date())).padStart(2, '0')}`;

  const activeEntries = timeEntries.filter(r => r[12] === 'Active');
  const todayEntries = timeEntries.filter(r => r[14] === todayStr);

  // Format time helper
  const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return format(parseISO(iso), 'h:mm a'); } catch (e) { return '—'; }
  };

  // Calculate elapsed for active session
  const getActiveElapsed = (clockInISO) => {
    try {
      const ms = Date.now() - new Date(clockInISO).getTime();
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      return `${h}h ${m}m`;
    } catch (e) { return '—'; }
  };

  // Get week date range
  const getWeekRange = (weekStr) => {
    try {
      const [yearStr, weekPart] = weekStr.split('-W');
      const year = parseInt(yearStr);
      const week = parseInt(weekPart);
      const jan4 = new Date(year, 0, 4);
      const weekStart = startOfISOWeek(jan4);
      const adjustedStart = addDays(weekStart, (week - 1) * 7);
      const adjustedEnd = addDays(adjustedStart, 6);
      return `${format(adjustedStart, 'MMM d')} — ${format(adjustedEnd, 'MMM d')}`;
    } catch (e) { return weekStr; }
  };

  // Handle admin edit save
  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    const sessionId = editingEntry[0];
    try {
      const rows = await (async () => {
        const text = await fetchWithRedirect(GATEWAY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({ secret: GATEWAY_SECRET, action: 'sheetsRead', params: { spreadsheetId: ONBOARDING_SHEET_ID, range: 'TimeEntries!A:A' } })
        });
        const result = JSON.parse(text);
        return result.success && result.data?.data ? result.data.data : [];
      })();

      const rowIndex = rows.findIndex(r => r[0] === sessionId);
      if (rowIndex < 0) { alert('Row not found'); return; }
      const sheetRow = rowIndex + 1;

      // Recalculate hours
      let totalHours = editForm.clockOut && editForm.clockIn
        ? ((new Date(editForm.clockOut).getTime() - new Date(editForm.clockIn).getTime() - (parseInt(editForm.breakMinutes) || 0) * 60000) / 3600000).toFixed(2)
        : '';

      await fetchWithRedirect(GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          secret: GATEWAY_SECRET,
          action: 'sheetsWrite',
          params: {
            spreadsheetId: ONBOARDING_SHEET_ID,
            range: `TimeEntries!D${sheetRow}:M${sheetRow}`,
            values: [[
              editForm.clockIn,        // D
              editingEntry[4],         // E: ClockInGPS (unchanged)
              editForm.clockOut,       // F
              editingEntry[6],         // G: ClockOutGPS (unchanged)
              totalHours,              // H
              editForm.project,        // I
              editForm.breakMinutes,   // J
              editingEntry[10],        // K: BreakLog (unchanged)
              editForm.notes,          // L
              editForm.clockOut ? 'Complete' : 'Active' // M
            ]]
          }
        })
      });

      setEditingEntry(null);
      fetchTimeEntries();
    } catch (e) {
      console.error('Edit save error:', e);
      alert('Failed to save changes');
    }
  };

  // Handle admin approve
  const handleApproveWeek = async (email, weekStr) => {
    try {
      const allRows = await (async () => {
        const text = await fetchWithRedirect(GATEWAY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({ secret: GATEWAY_SECRET, action: 'sheetsRead', params: { spreadsheetId: ONBOARDING_SHEET_ID, range: 'TimeEntries!A:R' } })
        });
        const result = JSON.parse(text);
        return result.success && result.data?.data ? result.data.data : [];
      })();

      for (let i = 1; i < allRows.length; i++) {
        const r = allRows[i];
        if (r[1] === email && r[13] === weekStr && r[15] === 'Confirmed' && !r[16]) {
          await fetchWithRedirect(GATEWAY_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({
              secret: GATEWAY_SECRET,
              action: 'sheetsWrite',
              params: {
                spreadsheetId: ONBOARDING_SHEET_ID,
                range: `TimeEntries!Q${i + 1}:R${i + 1}`,
                values: [[loggedInUser?.email || 'admin', new Date().toISOString()]]
              }
            })
          });
        }
      }
      fetchTimeEntries();
    } catch (e) {
      console.error('Approve error:', e);
    }
  };

  // Export CSV
  const exportTimeCSV = () => {
    const headers = ['SessionID','Email','Name','ClockIn','ClockOut','TotalHours','Project','BreakMinutes','Notes','Status','WeekNumber','Date','Confirmed','ApprovedBy'];
    const rows = timeEntries.map(r => [r[0],r[1],r[2],r[3],r[5],r[7],r[8],r[9],r[11],r[12],r[13],r[14],r[15],r[16]]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-entries-${todayStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderTimeTracking = () => {
    const subTabs = [
      { id: 'live', label: `Live (${activeEntries.length})` },
      { id: 'today', label: 'Today' },
      { id: 'weekly', label: 'Weekly' },
      { id: 'attention', label: 'Needs Attention' },
    ];

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Time Tracking</h2>
            <p style={{ color: mutedColor }}>Employee hours, attendance, and approvals</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={fetchTimeEntries} style={{ padding: '8px 16px', backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={exportTimeCSV} style={{ padding: '8px 16px', backgroundColor: cardBg, color: textColor, border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', overflowX: 'auto' }}>
          {subTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setTimeTrackingSubTab(tab.id)}
              style={{
                padding: '10px 20px',
                backgroundColor: timeTrackingSubTab === tab.id ? accentPrimary : cardBg,
                color: timeTrackingSubTab === tab.id ? '#fff' : textColor,
                border: timeTrackingSubTab === tab.id ? 'none' : `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Sub-tab content */}
        {timeTrackingSubTab === 'live' && (
          <div>
            {activeEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', backgroundColor: cardBg, borderRadius: '12px', color: mutedColor }}>
                <Clock size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                <div>No one is currently clocked in</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {activeEntries.map((r, i) => (
                  <div key={i} style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: accentSecondary, animation: 'pulse 2s infinite' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: textColor }}>{r[2]}</div>
                        <div style={{ fontSize: '0.8rem', color: mutedColor }}>{r[1]}</div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: mutedColor }}>Clocked In</div>
                        <div style={{ color: textColor, fontSize: '0.9rem' }}>{fmtTime(r[3])}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: mutedColor }}>Elapsed</div>
                        <div style={{ color: accentPrimary, fontWeight: '600' }}>{getActiveElapsed(r[3])}</div>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: '0.75rem', color: mutedColor }}>Project</div>
                        <div style={{ color: textColor, fontSize: '0.9rem' }}>{r[8] || '—'}</div>
                      </div>
                      {r[4] && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <span style={{ backgroundColor: `${accentSecondary}20`, color: accentSecondary, padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem' }}>GPS</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {timeTrackingSubTab === 'today' && (
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
                    {['Name', 'Clock In', 'Clock Out', 'Project', 'Hours', 'Break', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', color: mutedColor }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {todayEntries.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: mutedColor }}>No entries for today</td></tr>
                  ) : todayEntries.map((r, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}` }}>
                      <td style={{ padding: '12px', color: textColor, fontWeight: '500' }}>{r[2]}</td>
                      <td style={{ padding: '12px', color: textColor }}>{fmtTime(r[3])}</td>
                      <td style={{ padding: '12px', color: textColor }}>{fmtTime(r[5])}</td>
                      <td style={{ padding: '12px', color: textColor, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r[8] || '—'}</td>
                      <td style={{ padding: '12px', color: textColor, fontFamily: 'monospace', fontWeight: '600' }}>{r[7] ? `${r[7]}h` : '—'}</td>
                      <td style={{ padding: '12px', color: textColor }}>{r[9] ? `${r[9]}m` : '—'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500',
                          backgroundColor: r[12] === 'Complete' ? `${accentSecondary}20` : `${accentPrimary}20`,
                          color: r[12] === 'Complete' ? accentSecondary : accentPrimary
                        }}>{r[12]}</span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button onClick={() => { setEditingEntry(r); setEditForm({ clockIn: r[3] || '', clockOut: r[5] || '', project: r[8] || '', notes: r[11] || '', breakMinutes: r[9] || '0' }); }} style={{ background: 'none', border: 'none', color: accentPrimary, cursor: 'pointer', padding: '4px' }}>
                          <Edit3 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {timeTrackingSubTab === 'weekly' && (() => {
          // Group by employee + week
          const weekMap = {};
          for (const r of timeEntries) {
            if (!r[1] || !r[13]) continue;
            const key = `${r[1]}|${r[13]}`;
            if (!weekMap[key]) weekMap[key] = { email: r[1], name: r[2], week: r[13], entries: [], totalHours: 0, confirmed: true, approved: false };
            weekMap[key].entries.push(r);
            weekMap[key].totalHours += parseFloat(r[7]) || 0;
            if (r[12] === 'Complete' && r[15] !== 'Confirmed') weekMap[key].confirmed = false;
            if (r[16]) weekMap[key].approved = true;
          }
          const weeks = Object.values(weekMap).sort((a, b) => b.week.localeCompare(a.week) || a.name.localeCompare(b.name));

          return (
            <div style={{ backgroundColor: cardBg, borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
                      {['Employee', 'Week', 'Dates', 'Shifts', 'Total Hours', 'Status', ''].map(h => (
                        <th key={h} style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', color: mutedColor }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: mutedColor }}>No weekly data</td></tr>
                    ) : weeks.map((w, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}` }}>
                        <td style={{ padding: '12px', color: textColor, fontWeight: '500' }}>{w.name}</td>
                        <td style={{ padding: '12px', color: textColor, fontFamily: 'monospace', fontSize: '0.85rem' }}>{w.week}</td>
                        <td style={{ padding: '12px', color: mutedColor, fontSize: '0.85rem' }}>{getWeekRange(w.week)}</td>
                        <td style={{ padding: '12px', color: textColor }}>{w.entries.length}</td>
                        <td style={{ padding: '12px', color: accentPrimary, fontWeight: '700', fontFamily: 'monospace' }}>{w.totalHours.toFixed(2)}h</td>
                        <td style={{ padding: '12px' }}>
                          {w.approved ? (
                            <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', backgroundColor: `${accentSecondary}20`, color: accentSecondary }}>Approved</span>
                          ) : w.confirmed ? (
                            <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', backgroundColor: '#f59e0b20', color: '#f59e0b' }}>Confirmed</span>
                          ) : (
                            <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', backgroundColor: `${mutedColor}20`, color: mutedColor }}>Pending</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {w.confirmed && !w.approved && (
                            <button onClick={() => handleApproveWeek(w.email, w.week)} style={{ padding: '6px 12px', backgroundColor: accentSecondary, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500' }}>
                              Approve
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {timeTrackingSubTab === 'attention' && (() => {
          // Not clocked in today
          const clockedInTodayEmails = new Set(todayEntries.map(r => r[1]));
          const notClockedIn = employees.filter(e => !clockedInTodayEmails.has(e.email) && e.status === 'active');

          // Flagged: active sessions from previous days or >14 hours
          const flagged = timeEntries.filter(r => {
            if (r[12] !== 'Active') return false;
            const clockInDate = r[14];
            if (clockInDate && clockInDate < todayStr) return true; // previous day
            try {
              const elapsed = (Date.now() - new Date(r[3]).getTime()) / 3600000;
              if (elapsed > 14) return true;
            } catch (e) {}
            return false;
          });

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Not clocked in */}
              <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: textColor, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertTriangle size={18} color="#f59e0b" /> Not Clocked In Today ({notClockedIn.length})
                </h3>
                {notClockedIn.length === 0 ? (
                  <p style={{ color: mutedColor }}>All active employees have clocked in today</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {notClockedIn.map(e => (
                      <div key={e.email} style={{ padding: '8px 16px', backgroundColor: '#f59e0b15', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                        <span style={{ color: textColor, fontSize: '0.9rem' }}>{e.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Flagged entries */}
              <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: textColor, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertTriangle size={18} color={accentError} /> Flagged Sessions ({flagged.length})
                </h3>
                {flagged.length === 0 ? (
                  <p style={{ color: mutedColor }}>No flagged sessions</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {flagged.map((r, i) => (
                      <div key={i} style={{ padding: '12px 16px', backgroundColor: `${accentError}10`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                        <div>
                          <span style={{ fontWeight: '500', color: textColor }}>{r[2]}</span>
                          <span style={{ color: mutedColor, marginLeft: '12px', fontSize: '0.85rem' }}>Clocked in {fmtTime(r[3])} on {r[14]}</span>
                          <span style={{ color: accentError, marginLeft: '12px', fontSize: '0.85rem', fontWeight: '600' }}>{getActiveElapsed(r[3])}</span>
                        </div>
                        <button onClick={() => { setEditingEntry(r); setEditForm({ clockIn: r[3] || '', clockOut: '', project: r[8] || '', notes: 'Admin closed - forgot to clock out', breakMinutes: r[9] || '0' }); }} style={{ padding: '6px 12px', backgroundColor: accentError, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                          Close Session
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Edit Modal */}
        {editingEntry && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '32px', maxWidth: '500px', width: '100%' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '20px', color: textColor }}>Edit Time Entry</h3>
              <p style={{ color: mutedColor, marginBottom: '20px', fontSize: '0.9rem' }}>{editingEntry[2]} — {editingEntry[0]}</p>

              <div style={{ display: 'grid', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: textColor, fontSize: '0.85rem' }}>Clock In</label>
                  <input type="datetime-local" value={editForm.clockIn ? editForm.clockIn.slice(0, 16) : ''} onChange={e => setEditForm({ ...editForm, clockIn: e.target.value ? new Date(e.target.value).toISOString() : '' })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, backgroundColor: darkMode ? '#1e293b' : '#fff', color: textColor }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: textColor, fontSize: '0.85rem' }}>Clock Out</label>
                  <input type="datetime-local" value={editForm.clockOut ? editForm.clockOut.slice(0, 16) : ''} onChange={e => setEditForm({ ...editForm, clockOut: e.target.value ? new Date(e.target.value).toISOString() : '' })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, backgroundColor: darkMode ? '#1e293b' : '#fff', color: textColor }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: textColor, fontSize: '0.85rem' }}>Project</label>
                  <input type="text" value={editForm.project} onChange={e => setEditForm({ ...editForm, project: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, backgroundColor: darkMode ? '#1e293b' : '#fff', color: textColor }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: textColor, fontSize: '0.85rem' }}>Break Minutes</label>
                  <input type="number" value={editForm.breakMinutes} onChange={e => setEditForm({ ...editForm, breakMinutes: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, backgroundColor: darkMode ? '#1e293b' : '#fff', color: textColor }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: textColor, fontSize: '0.85rem' }}>Notes</label>
                  <textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={2} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, backgroundColor: darkMode ? '#1e293b' : '#fff', color: textColor, resize: 'vertical' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setEditingEntry(null)} style={{ flex: 1, padding: '12px', backgroundColor: 'transparent', border: `1px solid ${mutedColor}`, borderRadius: '8px', color: textColor, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleSaveEdit} style={{ flex: 1, padding: '12px', backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Save Changes</button>
              </div>
            </div>
          </div>
        )}

        {/* Pulse animation */}
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  };

  const renderSettings = () => {
    const selectedBg = darkMode ? 'rgba(102,126,234,0.2)' : 'rgba(0,180,216,0.1)';
    const selectedBorder = accentPrimary;
    const unselectedBorder = darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb';

    return (
      <div>
        <h2 style={{ fontSize: '24px', fontWeight: 700, color: textColor, marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Settings size={24} /> Settings
        </h2>
        <div style={{ background: cardBg, borderRadius: '12px', padding: '24px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}` }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: textColor, marginBottom: '16px' }}>Appearance</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {/* Light Mode Card */}
            <div
              onClick={() => setDarkMode(false)}
              style={{
                flex: '1 1 140px', maxWidth: '200px', padding: '20px', borderRadius: '10px', cursor: 'pointer',
                border: `2px solid ${!darkMode ? selectedBorder : unselectedBorder}`,
                background: !darkMode ? selectedBg : 'transparent',
                textAlign: 'center', transition: 'all 0.2s'
              }}
            >
              <Sun size={28} color={!darkMode ? accentPrimary : mutedColor} />
              <div style={{ marginTop: '8px', fontWeight: 600, fontSize: '14px', color: !darkMode ? accentPrimary : mutedColor }}>Light</div>
            </div>
            {/* Dark Mode Card */}
            <div
              onClick={() => setDarkMode(true)}
              style={{
                flex: '1 1 140px', maxWidth: '200px', padding: '20px', borderRadius: '10px', cursor: 'pointer',
                border: `2px solid ${darkMode ? selectedBorder : unselectedBorder}`,
                background: darkMode ? selectedBg : 'transparent',
                textAlign: 'center', transition: 'all 0.2s'
              }}
            >
              <Moon size={28} color={darkMode ? accentPrimary : mutedColor} />
              <div style={{ marginTop: '8px', fontWeight: 600, fontSize: '14px', color: darkMode ? accentPrimary : mutedColor }}>Dark</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'onboarding': return renderOnboarding();
      case 'employees': return renderEmployees();
      case 'contractors': return renderContractors();
      case 'admins': return renderAdmins();
      case 'time-tracking': return renderTimeTracking();
      case 'testing': return renderTesting();
      case 'settings': return renderSettings();
      default: return renderDashboard();
    }
  };

  return (
    <div 
      style={{ 
        minHeight: '100vh', 
        backgroundColor: bgColor, 
        display: 'flex',
        // Mobile touch scrolling fix
        WebkitOverflowScrolling: 'touch',
        overflowX: 'hidden'
      }}
      onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }}
    >
      {/* Mobile Header - Only shows on mobile */}
      {isMobile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '56px',
          backgroundColor: cardBg,
          borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          zIndex: 1000
        }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            style={{
              background: 'none',
              border: 'none',
              color: textColor,
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '44px',
              minHeight: '44px'
            }}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
            <span style={{ color: darkMode ? '#e6c4d9' : '#0a3a7d' }}>ly</span>
            <span style={{ color: darkMode ? '#e6c4d9' : '#2ec7c0' }}>t</span>
            <span style={{ color: mutedColor, fontSize: '0.85rem', marginLeft: '6px' }}>Admin</span>
          </div>
          {/* Dark Mode Toggle + Notifications */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <NotificationBell darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />
            {setDarkMode && (
            <button
              onClick={() => setDarkMode(!darkMode)}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              style={{
                background: 'none',
                border: 'none',
                color: textColor,
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '44px',
                minHeight: '44px'
              }}
            >
              {darkMode ? <Sun size={22} /> : <Moon size={22} />}
            </button>
          )}
          </div>
        </div>
      )}

      {/* Mobile Overlay - closes sidebar when clicking outside */}
      {isMobile && sidebarOpen && (
        <div 
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1001
          }}
        />
      )}

      {/* Sidebar - Fixed on desktop, slide-in overlay on mobile */}
      <div style={{ 
        width: '260px', 
        backgroundColor: cardBg, 
        borderRight: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        height: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        zIndex: isMobile ? 1002 : 1,
        // Mobile: slide in from left
        transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
        transition: isMobile ? 'transform 0.3s ease' : 'none',
        top: 0,
        left: 0
      }}>
        {/* Logo */}
        <div style={{ padding: '20px', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
            <span style={{ color: darkMode ? '#e6c4d9' : '#0a3a7d' }}>ly</span>
            <span style={{ color: darkMode ? '#e6c4d9' : '#2ec7c0' }}>t</span>
            <span style={{ color: mutedColor, fontSize: '0.9rem', marginLeft: '8px' }}>Admin</span>
          </div>
          {/* Close button on mobile */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: mutedColor,
                cursor: 'pointer',
                padding: '8px',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* User Info */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: `${accentPrimary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentPrimary, fontWeight: '600' }}>
              {loggedInUser?.name?.charAt(0) || 'A'}
            </div>
            <div>
              <div style={{ fontWeight: '500', color: textColor }}>{loggedInUser?.name || 'Admin'}</div>
              <div style={{ fontSize: '0.75rem', color: mutedColor }}>{loggedInUser?.email}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ flex: 1, padding: '12px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {navItems.map((item) => (
            item.divider ? (
              <div key={item.id} style={{ padding: '8px 16px 4px', fontSize: '0.7rem', fontWeight: '600', color: mutedColor, letterSpacing: '0.5px', textTransform: 'uppercase', userSelect: 'none' }}>
                {item.label.replace(/─/g, '').trim()}
              </div>
            ) : (
            <button
              key={item.id}
              onClick={() => {
                if (item.external) {
                  setCurrentPage(item.external);
                } else {
                  setActiveTab(item.id);
                }
                if (isMobile) setSidebarOpen(false);
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: isMobile ? '14px 16px' : '12px 16px',
                marginBottom: '4px',
                backgroundColor: activeTab === item.id ? `${accentPrimary}15` : 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                color: activeTab === item.id ? accentPrimary : textColor,
                textAlign: 'left',
                transition: 'all 0.2s',
                minHeight: isMobile ? '48px' : 'auto'
              }}
            >
              <item.icon size={18} />
              <span style={{ flex: 1, fontSize: '0.9rem' }}>{item.label}</span>
              {item.badge > 0 && (
                <span style={{ 
                  backgroundColor: accentError, 
                  color: '#fff', 
                  fontSize: '0.7rem', 
                  padding: '2px 8px', 
                  borderRadius: '10px',
                  fontWeight: '600'
                }}>
                  {item.badge}
                </span>
              )}
              {item.external && <ChevronRight size={14} color={mutedColor} />}
            </button>
            )
          ))}
        </div>

        {/* Logout */}
        <div style={{ padding: '16px', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: isMobile ? '14px' : '12px',
              backgroundColor: 'transparent',
              border: `1px solid ${accentError}`,
              borderRadius: '8px',
              color: accentError,
              cursor: 'pointer',
              fontSize: '0.9rem',
              minHeight: isMobile ? '48px' : 'auto'
            }}
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        marginLeft: isMobile ? 0 : '260px',
        padding: isMobile ? '72px 16px 88px' : '32px',
        minHeight: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        {renderContent()}
      </div>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, height: '56px',
            backgroundColor: cardBg, borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-around',
            zIndex: 1000, paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            {bottomTabs.map(tab => {
              const isActive = tab.id === 'more' ? moreSheetOpen : activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => {
                  if (tab.id === 'more') { setMoreSheetOpen(!moreSheetOpen); }
                  else { setActiveTab(tab.id); setMoreSheetOpen(false); }
                }} style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
                  color: isActive ? accentPrimary : mutedColor, position: 'relative',
                }}>
                  <tab.icon size={22} />
                  <span style={{ fontSize: '0.65rem', fontWeight: isActive ? 700 : 500 }}>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span style={{ position: 'absolute', top: '2px', right: 'calc(50% - 16px)', width: '16px', height: '16px', borderRadius: '50%', background: '#FFB800', color: '#fff', fontSize: '0.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* More slide-up sheet */}
          {moreSheetOpen && (
            <>
              <div onClick={() => setMoreSheetOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1001 }} />
              <div style={{
                position: 'fixed', bottom: '56px', left: 0, right: 0, zIndex: 1002,
                backgroundColor: cardBg, borderRadius: '16px 16px 0 0',
                padding: '20px', paddingBottom: '24px',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              }}>
                <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', margin: '0 auto 16px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  {navItems.filter(n => !n.divider && !n.external && !['dashboard', 'onboarding', 'time-tracking', 'employees'].includes(n.id)).map(item => (
                    <button key={item.id} onClick={() => { setActiveTab(item.id); setMoreSheetOpen(false); }} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                      padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                      background: activeTab === item.id ? `${accentPrimary}12` : 'transparent',
                      color: activeTab === item.id ? accentPrimary : textColor,
                    }}>
                      <item.icon size={22} />
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{item.label}</span>
                    </button>
                  ))}
                  <button onClick={() => { setDarkMode(!darkMode); }} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                    background: 'transparent', color: textColor,
                  }}>
                    {darkMode ? <Sun size={22} /> : <Moon size={22} />}
                    <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{darkMode ? 'Light' : 'Dark'}</span>
                  </button>
                  <button onClick={() => { setMoreSheetOpen(false); handleLogout(); }} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                    background: 'transparent', color: accentError,
                  }}>
                    <LogOut size={22} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Version Number - Triple Click to Show */}
      {showVersion && (
        <div style={{
          position: 'fixed',
          bottom: isMobile ? '64px' : '10px',
          right: '10px',
          fontSize: '0.7rem',
          opacity: 0.5,
          color: textColor,
          backgroundColor: cardBg,
          padding: '4px 8px',
          borderRadius: '4px',
          zIndex: 9999
        }}>
          AdminDashboard v3.8
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
